import { useEffect, useState } from 'react';
import { Check, Send } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import {
  getNotificationConfig,
  updateNotificationConfig,
  testWhatsApp,
} from '@/api/admin';

/**
 * WhatsApp is delivered through BusinessOnBot's send-a-template API.
 * The operator saves an API key + endpoint + default template + language,
 * then can fire a Test message to verify the wiring.
 */
export function WhatsAppConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const cfgQuery = useQuery({
    queryKey: ['notification-config'],
    queryFn: getNotificationConfig,
    staleTime: 30_000,
  });

  const [form, setForm] = useState({
    bonbApiKey: '',
    bonbEndpoint: 'https://whatsapp.bonb.io/v1/send/grhamoy',
    bonbDefaultTemplate: '',
    bonbLanguage: 'en_US',
    bonbDocumentTemplate: '',
    bonbPublicBaseUrl: '',
    bonbTplQuotation: '',
    bonbTplInvoice: '',
    bonbTplSalesOrder: '',
    bonbTplPo: '',
    bonbTplPrApproval: '',
    bonbTplEnquiryAck: '',
    bonbTplDailySummary: '',
  });
  const [testPhone, setTestPhone] = useState('');

  // Seed the form from the loaded config. API key comes back as a boolean
  // (`bonbApiKeySet`) — the actual value is never returned. The input stays
  // blank; the operator only re-enters if they want to change it.
  useEffect(() => {
    if (!cfgQuery.data) return;
    setForm({
      bonbApiKey: '',
      bonbEndpoint: cfgQuery.data.bonbEndpoint ?? 'https://whatsapp.bonb.io/v1/send/grhamoy',
      bonbDefaultTemplate: cfgQuery.data.bonbDefaultTemplate ?? '',
      bonbLanguage: cfgQuery.data.bonbLanguage ?? 'en_US',
      bonbDocumentTemplate: cfgQuery.data.bonbDocumentTemplate ?? '',
      bonbPublicBaseUrl: cfgQuery.data.bonbPublicBaseUrl ?? '',
      bonbTplQuotation: cfgQuery.data.bonbTplQuotation ?? '',
      bonbTplInvoice: cfgQuery.data.bonbTplInvoice ?? '',
      bonbTplSalesOrder: cfgQuery.data.bonbTplSalesOrder ?? '',
      bonbTplPo: cfgQuery.data.bonbTplPo ?? '',
      bonbTplPrApproval: cfgQuery.data.bonbTplPrApproval ?? '',
      bonbTplEnquiryAck: cfgQuery.data.bonbTplEnquiryAck ?? '',
      bonbTplDailySummary: cfgQuery.data.bonbTplDailySummary ?? '',
    });
  }, [cfgQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateNotificationConfig({
        bonbEndpoint: form.bonbEndpoint.trim() || undefined,
        bonbDefaultTemplate: form.bonbDefaultTemplate.trim() || undefined,
        bonbLanguage: form.bonbLanguage.trim() || undefined,
        bonbDocumentTemplate: form.bonbDocumentTemplate.trim() || undefined,
        bonbPublicBaseUrl: form.bonbPublicBaseUrl.trim() || undefined,
        bonbTplQuotation: form.bonbTplQuotation.trim() || undefined,
        bonbTplInvoice: form.bonbTplInvoice.trim() || undefined,
        bonbTplSalesOrder: form.bonbTplSalesOrder.trim() || undefined,
        bonbTplPo: form.bonbTplPo.trim() || undefined,
        bonbTplPrApproval: form.bonbTplPrApproval.trim() || undefined,
        bonbTplEnquiryAck: form.bonbTplEnquiryAck.trim() || undefined,
        // Only send api key if the operator typed a new one — an empty
        // string means "keep the current stored value".
        bonbApiKey: form.bonbApiKey.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-config'] });
      toast.push('WhatsApp configuration saved', 'success');
      setForm((f) => ({ ...f, bonbApiKey: '' })); // clear the secret input after save
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  const testMut = useMutation({
    mutationFn: () => testWhatsApp(testPhone, form.bonbDefaultTemplate.trim() || undefined),
    onSuccess: (r) => toast.push(r.message ?? 'Test template sent', 'success'),
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Test failed'), 'error');
    },
  });

  const apiKeySet = cfgQuery.data?.bonbApiKeySet ?? false;

  return (
    <div>
      <SectionHeader
        title="WhatsApp Configuration"      />

      <div data-compact-form="true" className="grid gap-4 md:grid-cols-2">
        {/* ── Provider config ─────────────────────────────── */}
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl">
          <div className="mb-3 text-sm font-extrabold text-[var(--text-primary)]">
            BusinessOnBot BSP
          </div>

          <FormGroup label="API Key" required>
            <Input
              type="password"
              value={form.bonbApiKey}
              onChange={(e) => setForm((f) => ({ ...f, bonbApiKey: e.target.value }))}
              placeholder={apiKeySet ? '•••••••• (saved — leave blank to keep)' : 'x-api-key header value from BSP portal'}
              autoComplete="off"
            />
          </FormGroup>

          <FormGroup label="Endpoint URL" required>
            <Input
              value={form.bonbEndpoint}
              onChange={(e) => setForm((f) => ({ ...f, bonbEndpoint: e.target.value }))}
              placeholder="https://whatsapp.bonb.io/v1/send/grhamoy"
            />
          </FormGroup>

          <div className="grid grid-cols-2 gap-3">
            <FormGroup label="Default Text Template">
              <Input
                value={form.bonbDefaultTemplate}
                onChange={(e) => setForm((f) => ({ ...f, bonbDefaultTemplate: e.target.value }))}
                placeholder="e.g. agent_inactivity_v1"
              />
            </FormGroup>
            <FormGroup label="Language">
              <Input
                value={form.bonbLanguage}
                onChange={(e) => setForm((f) => ({ ...f, bonbLanguage: e.target.value }))}
                placeholder="en_US"
              />
            </FormGroup>
          </div>

          <FormGroup label="Document Template">
            <Input
              value={form.bonbDocumentTemplate}
              onChange={(e) => setForm((f) => ({ ...f, bonbDocumentTemplate: e.target.value }))}
              placeholder="e.g. doc_ready_v1 — must have a document header"
            />
          </FormGroup>

          <FormGroup label="Public Base URL (HTTPS)">
            <Input
              value={form.bonbPublicBaseUrl}
              onChange={(e) => setForm((f) => ({ ...f, bonbPublicBaseUrl: e.target.value }))}
              placeholder="e.g. https://grhamoy.wizone.ai"
            />
          </FormGroup>

          <div className="mt-1 border-t border-[var(--border-color)] pt-3">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              Approved Templates — registered once, reused by the app
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormGroup label="Quotation (Document)">
                <Input value={form.bonbTplQuotation} onChange={(e) => setForm((f) => ({ ...f, bonbTplQuotation: e.target.value }))} placeholder="grhamoy_quotation" />
              </FormGroup>
              <FormGroup label="Invoice">
                <Input value={form.bonbTplInvoice} onChange={(e) => setForm((f) => ({ ...f, bonbTplInvoice: e.target.value }))} placeholder="grhamoy_invoice_new" />
              </FormGroup>
              <FormGroup label="Sales Order">
                <Input value={form.bonbTplSalesOrder} onChange={(e) => setForm((f) => ({ ...f, bonbTplSalesOrder: e.target.value }))} placeholder="grhamoy_so_new" />
              </FormGroup>
              <FormGroup label="Purchase Order">
                <Input value={form.bonbTplPo} onChange={(e) => setForm((f) => ({ ...f, bonbTplPo: e.target.value }))} placeholder="grhamoy_po_new" />
              </FormGroup>
              <FormGroup label="PR Approval (Text)">
                <Input value={form.bonbTplPrApproval} onChange={(e) => setForm((f) => ({ ...f, bonbTplPrApproval: e.target.value }))} placeholder="grhamoy_pr_approval" />
              </FormGroup>
              <FormGroup label="Enquiry Ack (Text)">
                <Input value={form.bonbTplEnquiryAck} onChange={(e) => setForm((f) => ({ ...f, bonbTplEnquiryAck: e.target.value }))} placeholder="grhamoy_enquiry_ack" />
              </FormGroup>
              <FormGroup label="Daily Summary (7 vars)">
                <Input value={form.bonbTplDailySummary} onChange={(e) => setForm((f) => ({ ...f, bonbTplDailySummary: e.target.value }))} placeholder="daily_management_summary" />
              </FormGroup>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-[11px] text-[var(--text-muted)]">
              {apiKeySet
                ? <><Check size={12} className="mr-1 inline-block align-[-2px] text-success" /> API key stored — leave blank to keep, type a new one to replace.</>
                : 'API key not set.'}
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? 'Saving…' : 'Save Configuration'}
            </Button>
          </div>
        </div>

        {/* ── Verify config ───────────────────────────────── */}
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl">
          <div className="mb-3 text-sm font-extrabold text-[var(--text-primary)]">
            Test Configuration
          </div>
          <p className="mb-4 text-[12px] text-[var(--text-muted)]">
            Fires the <strong>default template</strong> above to the phone number below via
            BusinessOnBot. Confirms the API key + endpoint + template name are all wired
            correctly. The recipient will actually receive a WhatsApp message.
          </p>

          <FormGroup label="Test Phone (with country code)">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="e.g. 91 98765 43210"
            />
          </FormGroup>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="glass"
              size="sm"
              onClick={() => testMut.mutate()}
              disabled={
                testMut.isPending ||
                !testPhone.trim() ||
                (!apiKeySet && !form.bonbApiKey.trim()) ||
                !form.bonbDefaultTemplate.trim()
              }
              title={
                !apiKeySet && !form.bonbApiKey.trim()
                  ? 'Save an API key first'
                  : !form.bonbDefaultTemplate.trim()
                  ? 'Set a default template first'
                  : 'Send a test template'
              }
            >
              {testMut.isPending ? 'Sending…' : <><Send size={13} className="mr-1 inline-block align-[-2px]" /> Send Test Message</>}
            </Button>
          </div>

          <div className="mt-4 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] p-3 text-[11px] leading-relaxed text-[var(--text-muted)]">
            <div className="mb-1 font-bold text-[var(--text-secondary)]">Notes</div>
            <ul className="list-disc pl-4 space-y-1">
              <li>Templates must be pre-approved on the BusinessOnBot portal before use.</li>
              <li><strong>Default Text Template</strong> — used for the Test button and for portal-link / reminder messages. Body vars: <code>{'{{1}}'}</code> = title, <code>{'{{2}}'}</code> = doc# / RFQ#, <code>{'{{3}}'}</code> = note.</li>
              <li><strong>Document Template</strong> — used when the app sends a doc PDF (POs, invoices, quotes). Must be declared on the portal with a <strong>document</strong> header. Same body vars as above.</li>
              <li><strong>Public Base URL</strong> — HTTPS origin BusinessOnBot's servers can reach. The app serves generated PDFs at <code>&lt;base&gt;/api/public/wa-doc/&lt;token&gt;.pdf</code> for 10 minutes so BSP can download and attach them.</li>
              <li>If either the document template or public URL is blank, doc sends fall back to text-only.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
